import React from 'react';
import { Sparkles, X, Send, Loader2 } from 'lucide-react';
import useSpeechSynthesis from '../hooks/useSpeechSynthesis';
import SpeakButton from './SpeakButton';

export default function CourseTutorPanel({
  open,
  courseTitle,
  messages,
  input,
  loading,
  onInputChange,
  onSend,
  onClose,
}) {
  const { supported: ttsSupported, speaking, speak, stop } = useSpeechSynthesis();

  if (!open) return null;

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <div
      data-testid="course-tutor-panel"
      className="fixed right-6 z-[95] w-[450px] max-w-[calc(100vw-3rem)] h-[600px] bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-2xl border-2 border-purple-500/50 flex flex-col overflow-hidden"
      style={{ bottom: 'calc(252px + 1.5rem)' }}
    >
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold">AI Teaching Assistant</h3>
            <p className="text-xs opacity-90">{courseTitle}</p>
          </div>
        </div>
        <button
          onClick={onClose}
          data-testid="course-tutor-close"
          className="hover:bg-white/20 p-2 rounded-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                msg.type === 'user' ? 'bg-purple-600 text-white' : 'bg-gray-700 text-white'
              }`}
            >
              <p className="text-sm leading-relaxed">{msg.text}</p>
              {msg.type === 'tutor' && (
                <div className="mt-2 flex justify-end">
                  <SpeakButton
                    text={msg.text}
                    supported={ttsSupported}
                    speaking={speaking}
                    onSpeak={speak}
                    onStop={stop}
                    testId={`speak-tutor-msg-${msg.id}`}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-700 text-white rounded-2xl px-4 py-3 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <p className="text-sm">Your instructor is thinking...</p>
            </div>
          </div>
        )}
      </div>

      <div className="p-4 border-t border-gray-700 bg-gray-900/50">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyPress={handleKey}
            placeholder="Ask about topics, concepts, or anything..."
            disabled={loading}
            data-testid="course-tutor-input"
            className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
          />
          <button
            onClick={onSend}
            disabled={loading}
            data-testid="course-tutor-send"
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-3 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2 text-center">
          AI-powered course tutor • Gemini 2.5 Flash • Bound by the 112 Sacred Directives (Code Book Vol 1)
        </p>
      </div>
    </div>
  );
}
